<template>
  <v-container fluid>
    <PageHeaderSlot>
      <template slot="top-right-slot">
        <small v-if="_enableSettingBySchool && schoolName">{{ $t('campus') }}: {{ schoolName }}</small>
      </template>
    </PageHeaderSlot>

    <v-form ref="dataForm" @submit.prevent="">
      <v-card class="mb-4" v-for="method in methods">
        <v-card-text class="px-2">
          <v-row class="ma-0">
            <v-col cols="12">
              <v-row class="ma-0 pa-0 justify-space-between">
                <v-checkbox v-model="formData.accept_payment_methods" :value="method" :readonly="!canEdit">
                  <template v-slot:label>
                    <div class="text-subtitle-1 colorBlack--text font-weight-medium text-capitalize">
                      {{ $t(`paymentInfo.${method}`) }}
                      <small class="text-caption text-lowercase" v-if="method !== 'cash'">
                        {{ $t('useWithOnlinePayment') }}
                      </small>
                    </div>
                  </template>
                </v-checkbox>
                <FormButton
                  v-if="method === 'bank_transfer' && canEdit"
                  text="addBank"
                  isAdd
                  class="ml-4 my-2"
                  @click="handleAddBankInfo()"
                />
              </v-row>
              <v-divider v-if="method !== 'cash'"></v-divider>
            </v-col>

            <template v-if="method === 'payme'">
              <v-col cols="12">
                <FormInput
                  type="tel"
                  label="paymePhone"
                  placeholder="paymePhone"
                  :fieldValue.sync="formData.payme_phone"
                  isIntegerOnly
                  dispatchUpdateOnChange
                  :readonly="!canEdit"
                />
              </v-col>
              <v-col cols="12">
                <FormInput
                  label="paymeLink"
                  placeholder="paymeLink"
                  :fieldValue.sync="formData.payme_link"
                  dispatchUpdateOnChange
                  :readonly="!canEdit"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4" lg="3">
                <FormUploader
                  uploadText="uploadPaymeImg"
                  fileInputKey="upload_payme"
                  :files.sync="formData.payme_code"
                  dispatchUpdateOnChange
                  single
                  :readonly="!canEdit"
                />
              </v-col>
            </template>
            <template v-else-if="method === 'fps'">
              <v-col cols="12">
                <FormInput
                  type="number"
                  label="fpsId"
                  placeholder="fpsId"
                  :fieldValue.sync="formData.fps_id"
                  isIntegerOnly
                  dispatchUpdateOnChange
                  :readonly="!canEdit"
                />
              </v-col>
              <v-col cols="12">
                <FormInput
                  label="fpsPhoneEmail"
                  placeholder="fpsPhoneEmail"
                  :fieldValue.sync="formData.fps_phone_email"
                  dispatchUpdateOnChange
                  :readonly="!canEdit"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4" lg="3">
                <FormUploader
                  uploadText="uploadFPSImg"
                  fileInputKey="upload_fps"
                  :files.sync="formData.fps_code"
                  dispatchUpdateOnChange
                  single
                  :readonly="!canEdit"
                />
              </v-col>
            </template>
            <template v-else-if="method === 'wechat_pay'">
              <v-col cols="12" sm="6" md="4" lg="3">
                <FormUploader
                  uploadText="uploadWechatImg"
                  fileInputKey="upload_wechat"
                  :files.sync="formData.wechat_pay_code"
                  dispatchUpdateOnChange
                  single
                  :readonly="!canEdit"
                />
              </v-col>
            </template>
            <template v-else-if="method === 'alipay'">
              <v-col cols="12" sm="6" md="4" lg="3">
                <FormUploader
                  uploadText="uploadAlipayImg"
                  fileInputKey="upload_alipay"
                  :files.sync="formData.alipay_code"
                  dispatchUpdateOnChange
                  single
                  :readonly="!canEdit"
                />
              </v-col>
            </template>
            <template v-else-if="method === 'bank_transfer'">
              <v-col cols="12">
                <Datatable
                  class="mt-6"
                  :isLoading="tableLoading"
                  :tableData="tableData"
                  :tableHeaders="tableHeaders"
                  disablePagination
                  @edit-clicked="handleAddBankInfo($event)"
                  @delete-clicked="handleDeleteBankInfo($event)"
                />
              </v-col>
            </template>
          </v-row>
        </v-card-text>
      </v-card>

      <div class="d-flex align-center mt-4" v-if="canEdit">
        <FormButton @click="save()" />
      </div>
    </v-form>

    <!-- Modal: 新增銀行 -->
    <FormDialog :title="bankFormData.type === 'edit' ? 'editBank' : 'addBank'" ref="bankDialog" formRef="bankForm">
      <v-row class="ma-0">
        <v-col cols="12" md="6">
          <FormInput label="bankName" placeholder="bankName" :fieldValue.sync="bankFormData.bank_name" required />
        </v-col>
        <v-col cols="12" md="6">
          <FormInput
            label="beneficiaryName"
            placeholder="beneficiaryName"
            :fieldValue.sync="bankFormData.beneficiary_name"
            required
          />
        </v-col>
        <v-col cols="12" md="6">
          <FormInput
            label="accountNumber"
            placeholder="accountNumber"
            :fieldValue.sync="bankFormData.account_number"
            required
          />
        </v-col>
      </v-row>
    </FormDialog>
  </v-container>
</template>

<script>
import FormTextarea from '@/components/formField/FormTextarea.vue'
import FormInput from '@/components/formField/FormInput.vue'
import Datatable from '@/components/Datatable.vue'
import FormDialog from '@/components/FormDialog.vue'
import FormUploader from '@/components/formField/FormUploader.vue'

export default {
  name: 'PaymentSetting',
  components: {
    FormTextarea,
    FormInput,
    Datatable,
    FormDialog,
    FormUploader,
  },
  computed: {
    _enableSettingBySchool() {
      if (this.isWebsitePayment) return false
      return !this.gDisableSettingByCenter
    },
    isWebsitePayment() {
      return this.$route.name === 'WebsitePaymentSetting'
    },
    tableHeaders() {
      const vm = this
      const headers = [
        { value: 'bank_name', text: vm.$t('bankName') },
        { value: 'beneficiary_name', text: vm.$t('beneficiaryName') },
        { value: 'account_number', text: vm.$t('accountNumber') },
      ]
      if (this.canEdit) {
        headers.push({ value: 'minActionEditAndDelete', text: '', align: 'end' })
      }
      return headers
    },
    canEdit() {
      if (this.isWebsitePayment) return true
      // [T46] admin account 只可read
      return !this.isAppMode(['T46']) || this.userType !== 'admin'
    },
  },
  data: vm => ({
    userType: vm.gfGetUserType(),
    methods: ['payme', 'fps', 'wechat_pay', 'alipay', 'bank_transfer', 'cash'],
    formData: {
      accept_payment_methods: [],
      payme_phone: '',
      payme_code: [],
      payme_link: '',
      fps_id: '',
      fps_code: [],
      fps_phone_email: '',
      wechat_pay_phone: '',
      wechat_pay_code: [],
      alipay_phone: '',
      alipay_code: [],
    },
    schoolName: '',

    tableLoading: true,
    tableData: [],
    rowId: 0,
    bankFormData: {
      type: '',
      bank_name: '',
      beneficiary_name: '',
      account_number: '',
    },
  }),
  methods: {
    async getSchool() {
      const selectedSchool = this.gfGetUserSelectedSchool()
      if (selectedSchool > -1) {
        try {
          const data = await this.$Fetcher.GetSchoolById(selectedSchool)
          this.schoolName = data.name
          if (!data.payment_use_share) {
            this.insertData(data)
          } else {
            await this.getSetting()
          }
        } catch (err) {
          this.$common.error(err)
        }
      }
      this.$store.dispatch('toggleLoadingPage', false)
      this.tableLoading = false
    },
    async getSetting() {
      try {
        const data = await this.$Fetcher.GetSetting()
        this.insertData(data)
      } catch (err) {
        this.$common.error(err)
      } finally {
        this.$store.dispatch('toggleLoadingPage', false)
        this.tableLoading = false
      }
    },
    insertData(data) {
      let _data = data
      if (this.isWebsitePayment) {
        _data = data.online_course_payment_data
      }

      if (_data) {
        for (const key in this.formData) {
          if (this.$validate.DataValid(_data[key])) {
            this.formData[key] = _data[key]
          }
        }

        this.formData.payme_code = []
        this.formData.fps_code = []
        this.formData.wechat_pay_code = []
        this.formData.alipay_code = []

        if (_data['payment_info_payme']) {
          this.$set(this.formData, 'payme_phone', _data['payment_info_payme'].number)
          this._insertCodeFile(_data, 'payme')
          if (_data['payment_info_payme'].link) {
            this.$set(this.formData, 'payme_link', _data['payment_info_payme'].link)
          }
        }
        if (_data['payment_info_fps']) {
          this.$set(this.formData, 'fps_id', _data['payment_info_fps'].number)
          this._insertCodeFile(_data, 'fps')
          if (_data['payment_info_fps'].phone_email) {
            this.$set(this.formData, 'fps_phone_email', _data['payment_info_fps'].phone_email)
          }
        }
        if (_data['payment_info_wechat_pay']) {
          this.$set(this.formData, 'wechat_pay_phone', _data['payment_info_wechat_pay'].number)
          this._insertCodeFile(_data, 'wechat_pay')
        }
        if (_data['payment_info_alipay']) {
          this.$set(this.formData, 'alipay_phone', _data['payment_info_alipay'].number)
          this._insertCodeFile(_data, 'alipay')
        }
        if (_data['payment_info_bank_transfer']) {
          _data['payment_info_bank_transfer'].forEach(el => {
            this.tableData.push({
              id: this.rowId,
              ...el,
            })

            this.rowId += 1
          })
        }
      }
    },
    _insertCodeFile(data, method) {
      this.formData[`${method}_code`] = []
      if (data[`payment_info_${method}`].code) {
        if (typeof data[`payment_info_${method}`].code === 'string') {
          this.formData[`${method}_code`].push({
            file: data[`payment_info_${method}`].code,
            name: data[`payment_info_${method}`].code,
          })
        } else if (data[`payment_info_${method}`].code.file) {
          this.formData[`${method}_code`].push(data[`payment_info_${method}`].code)
        }
      }
    },

    async save() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      if (!this.canEdit) {
        this.$store.dispatch('toggleSnack', { message: 'noPermission' })
        return
      }

      if (this._enableSettingBySchool) {
        const check = await this.gfCheckSchoolSelected()
        if (!check) return
      }

      const valid = this.$refs['dataForm'].validate()
      if (!valid) {
        this.$store.dispatch('toggleSnack', { message: 'required' })
        return
      }
      this.$store.dispatch('setLoading', true)

      const payload = {
        payment_info_payme: {
          number: this.formData.payme_phone,
          code: this.formData.payme_code.length ? this.formData.payme_code[0] : '',
          link: this.formData.payme_link,
        },
        payment_info_fps: {
          number: this.formData.fps_id,
          code: this.formData.fps_code.length ? this.formData.fps_code[0] : '',
          phone_email: this.formData.fps_phone_email,
        },
        payment_info_wechat_pay: {
          number: this.formData.wechat_pay_phone,
          code: this.formData.wechat_pay_code.length ? this.formData.wechat_pay_code[0] : '',
        },
        payment_info_alipay: {
          number: this.formData.alipay_phone,
          code: this.formData.alipay_code.length ? this.formData.alipay_code[0] : '',
        },
        payment_info_bank_transfer: this.tableData.map(el => {
          return {
            bank_name: el.bank_name,
            beneficiary_name: el.beneficiary_name,
            account_number: el.account_number,
          }
        }),
        accept_payment_methods: this.formData.accept_payment_methods,
      }

      const schoolPayload = {
        payment_method: [],
      }
      if (this._enableSettingBySchool) {
        this.methods.forEach(method => {
          let obj = {
            type: method,
            active: payload.accept_payment_methods.includes(method),
          }
          if (payload[`payment_info_${method}`]) {
            if (method === 'bank_transfer') {
              obj['info_list'] = payload[`payment_info_${method}`]
            } else {
              obj = { ...obj, ...payload[`payment_info_${method}`] }
            }
          }
          schoolPayload['payment_method'].push(obj)
        })
      }

      try {
        if (this.isWebsitePayment) {
          await this.$Fetcher.UpdateSetting({ online_course_payment_data: payload })
        } else if (this._enableSettingBySchool) {
          const selectedSchool = this.gfGetUserSelectedSchool()
          await this.$Fetcher.UpdateSchool(selectedSchool, schoolPayload)
        } else {
          await this.$Fetcher.UpdateSetting(payload)
        }

        this.$store.dispatch('setDataIsUpdated', false)
        this.$store.dispatch('toggleSnack', {
          message: 'message.saveSuccess',
          type: 'success',
          refresh: true,
        })
      } catch (err) {
        this.$common.error(err)
        this.showAlert({
          title: 'message.saveFail',
          message: err,
          isError: true,
        })
      } finally {
        this.$store.dispatch('setLoading', false)
      }
    },

    // ======== 新增銀行 =======
    async handleAddBankInfo(id = null, newOpen = true) {
      if (newOpen) {
        if (this.$store.getters.isLoading) {
          this.$store.dispatch('toggleSnack', { message: 'processing' })
          return
        }
        if (!this.canEdit) {
          this.$store.dispatch('toggleSnack', { message: 'noPermission' })
          return
        }
        this.bankFormData.bank_name = ''
        this.bankFormData.beneficiary_name = ''
        this.bankFormData.account_number = ''
        await this.$refs.bankDialog.resetForm()

        if (this.$validate.DataValid(id)) {
          const target = this.tableData.find(el => el.id === id)
          if (target) {
            this.bankFormData.bank_name = target.bank_name
            this.bankFormData.beneficiary_name = target.beneficiary_name
            this.bankFormData.account_number = target.account_number
          }
        }
      } else {
        this.$refs.bankDialog.setLoading(false)
      }

      const confirm = await this.$refs.bankDialog.show()
      if (confirm) {
        if (this.$validate.DataValid(id)) {
          for (let i = 0; i < this.tableData.length; i++) {
            if (this.tableData[i].id === id) {
              this.$set(this.tableData[i], 'bank_name', this.bankFormData.bank_name)
              this.$set(this.tableData[i], 'beneficiary_name', this.bankFormData.beneficiary_name)
              this.$set(this.tableData[i], 'account_number', this.bankFormData.account_number)
              break
            }
          }
        } else {
          this.tableData.push({
            id: this.rowId,
            bank_name: this.bankFormData.bank_name,
            beneficiary_name: this.bankFormData.beneficiary_name,
            account_number: this.bankFormData.account_number,
          })
          this.rowId += 1
        }
        this.$store.dispatch('setDataIsUpdated', true)
        this.$refs.bankDialog.hide()
      }
    },
    handleDeleteBankInfo(id) {
      const targetPos = this.tableData.findIndex(el => el.id === id)
      if (targetPos > -1) {
        this.tableData.splice(targetPos, 1)
        this.$store.dispatch('setDataIsUpdated', true)
      }
    },

    async handleSiteLoaded() {
      this.$common.log('>>>>>>>>>>>>>>>>>>>>>>>>>>>> handleSiteLoaded: PaymentSetting')
      this.userType = this.gfGetUserType()
      this.formData.accept_payment_methods = []
      this.formData.payme_phone = ''
      this.formData.payme_code = []
      this.formData.payme_link = ''
      this.formData.fps_id = ''
      this.formData.fps_code = []
      this.formData.fps_phone_email = ''
      this.formData.wechat_pay_phone = ''
      this.formData.wechat_pay_code = []
      this.formData.alipay_phone = ''
      this.formData.alipay_code = []
      this.schoolName = ''
      this.tableData = []

      if (this._enableSettingBySchool) {
        await this.gfCheckSchoolSelected(true)
        this.getSchool()
      } else {
        this.getSetting()
      }
    },
  },

  destroyed() {
    window.removeEventListener('onSiteLoaded', this.handleSiteLoaded)
  },
  mounted() {
    this.$store.dispatch('toggleLoadingPage', true)
    window.addEventListener('onSiteLoaded', this.handleSiteLoaded)
  },

  // ------ navigation guard ------
  beforeRouteLeave(to, from, next) {
    if (this.$store.getters.isLoading) {
      this.$store.dispatch('toggleSnack', { message: 'processing' })
      next(false)
    } else if (this.$store.getters.dataIsUpdated) {
      this.$store.dispatch('showLeaveAlert', { toLink: { name: to.name } })
      next(false)
    } else {
      next()
    }
  },
}
</script>
